import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import ProductsComponent from '@shared/components/products';
import * as PropTypes from 'prop-types';
import { getProductsList } from '@shared/utils/detailUtils';
import { TelemetryImage } from '@shared/components/telemetryImage';
import { Stack, Text, mergeStyleSets, DefaultButton, ScreenWidthMinXLarge } from '@fluentui/react';
import classNames from 'classnames';
import { NeutralColors } from '@fluentui/theme';
import { bulidStartingPriceElement, getStartingPriceElement, shouldBlockPricing } from '@shared/utils/pricing';
import { BaseCtaButton } from '@shared/components/baseCtaButton';
import { Constants } from '@shared/utils/constants';
import { Link, useLocation } from 'react-router-dom';
import { logger } from '@src/logger';
import { RatingsInfo } from '@shared/components/ratings/ratingsInfo';
import { useSelector } from 'react-redux';
const containerTokens = {
    childrenGap: 16,
    padding: 16
};
const verticalItemsTokens = {
    childrenGap: 8,
    paddingTop: 16,
    paddingBottom: 16
};
const linkedItemStyles = mergeStyleSets({
    itemImage: {
        width: 60,
        height: 60,
        display: 'flex',
        backgroundColor: NeutralColors.white
    },
    linkItemTitle: {
        height: 48,
        fontWeight: 600,
        color: NeutralColors.gray160,
        overflow: 'hidden',
        display: '-webkit-box',
        textOverflow: 'ellipsis',
        '-webkit-box-orient': 'vertical',
        '-webkit-line-clamp': '2'
    },
    linkItemContainer: {
        justifyContent: 'space-around'
    },
    moreProductsStyle: {
        paddingTop: 3
    },
    imageStackItemStyle: {
        '& .appTileHoverImage': {
            width: 50,
            height: 50,
            margin: 'auto'
        }
    },
    productsAndStarsStackItemStyle: {
        marginBottom: 8
    },
    startingPriceElementContainer: {
        maxWidth: '237px',
        display: 'block',
        '& .button-link': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'start'
        },
        '& .buttonLinkContainer': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'start'
        },
        '& .dark': {
            marginTop: 0
        },
        [`@media (max-width: ${ScreenWidthMinXLarge}px)`]: {
            display: 'none'
        }
    },
    starsAndRattingContainer: {
        height: 20,
        '& .ratingInfoSection .detailsRatingStar': {
            marginBottom: 0
        }
    },
    rattingText: {
        color: NeutralColors.gray130
    },
    rattingStarText: {
        marginRight: 4,
        padding: 0,
        fontSize: 12
    },
    pricingAndCtaToken: {
        marginTop: 12
    },
    ctaButton: {
        maxWidth: 115
    }
});
const tileStyle = {
    root: {
        selectors: {
            '&:hover': {
                backgroundColor: NeutralColors.gray10
            }
        }
    }
};
const ctaButtonStyle = {
    textContainer: {
        overflow: 'hidden'
    },
    label: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
    }
};
const getProducts = (products)=>{
    return products && Object.keys(products).length > 0 && getProductsList(products);
};
const getRatingsData = (context, ratingsAverage, ratingsNum)=>{
    return ratingsNum >= 1 && {
        avgRating: ratingsAverage,
        ratingsCount: ratingsNum,
        key: 'internal',
        text: context.loc('Tile_ASRatings', 'ratings')
    };
};
const logTelemetryInfo = ({ linkedItem, location })=>{
    const { title, entityId } = linkedItem;
    const payload = {
        page: location.pathname,
        action: Constants.Telemetry.Action.Click,
        actionModifier: Constants.Telemetry.ActionModifier.SaaSLinkingTile,
        details: JSON.stringify({
            title,
            entityId
        })
    };
    logger.info('', payload);
};
export const LinkedItem = ({ linkedItem, isInAppDetail, billingCountryCode, isSaaSRendering, ctaButtonClick, isLoadingUserData, getLinkedItemLink, productId, locale }, context)=>{
    const startingPricingData = useSelector(({ config: { startingPricingData } })=>startingPricingData);
    const [isCardHover, setIsCardHover] = useState(false);
    const location = useLocation();
    const itemProducts = getProducts(linkedItem.products);
    const ratingsData = getRatingsData(context, linkedItem.AverageRating, linkedItem.NumberOfRatings);
    const startingPrice = shouldBlockPricing(linkedItem) ? null : startingPricingData === null || startingPricingData === void 0 ? void 0 : startingPricingData[linkedItem.entityId];
    const pricingElement = getStartingPriceElement({
        app: linkedItem,
        billingCountryCode,
        startingPrice,
        context,
        locale
    });
    return /*#__PURE__*/ _jsx(Link, {
        to: getLinkedItemLink({
            linkedItem,
            productId,
            context
        }),
        onClick: ()=>{
            logTelemetryInfo({
                linkedItem,
                location
            });
        },
        children: /*#__PURE__*/ _jsxs(Stack, {
            tokens: containerTokens,
            horizontal: true,
            className: classNames({
                'ms-depth-8': isInAppDetail
            }),
            onMouseEnter: ()=>setIsCardHover(true),
            onMouseLeave: ()=>setIsCardHover(false),
            styles: tileStyle,
            children: [
                /*#__PURE__*/ _jsx(Stack.Item, {
                    className: classNames([
                        linkedItemStyles.imageStackItemStyle
                    ]),
                    children: /*#__PURE__*/ _jsx(TelemetryImage, {
                        src: linkedItem.iconURL,
                        alt: "linked item icon",
                        className: classNames([
                            linkedItemStyles.itemImage
                        ], 'ms-depth-8'),
                        imgClassName: "appTileHoverImage"
                    })
                }),
                /*#__PURE__*/ _jsx(Stack.Item, {
                    grow: 1,
                    children: /*#__PURE__*/ _jsxs(Stack, {
                        tokens: verticalItemsTokens,
                        className: linkedItemStyles.linkItemContainer,
                        children: [
                            /*#__PURE__*/ _jsx(Stack.Item, {
                                children: /*#__PURE__*/ _jsx(Text, {
                                    variant: "large",
                                    className: classNames([
                                        linkedItemStyles.linkItemTitle
                                    ]),
                                    children: linkedItem.title
                                })
                            }),
                            (itemProducts === null || itemProducts === void 0 ? void 0 : itemProducts.length) && /*#__PURE__*/ _jsx(Stack.Item, {
                                grow: 1,
                                className: classNames([
                                    linkedItemStyles.moreProductsStyle
                                ]),
                                children: /*#__PURE__*/ _jsx(ProductsComponent, {
                                    withTooltip: true,
                                    textSize: "smallPlus",
                                    withText: true,
                                    productsItems: itemProducts
                                })
                            }),
                            /*#__PURE__*/ _jsx(Stack.Item, {
                                className: classNames([
                                    linkedItemStyles.starsAndRattingContainer
                                ]),
                                children: ratingsData && /*#__PURE__*/ _jsx(RatingsInfo, {
                                    avgRating: ratingsData.avgRating,
                                    ratingsCount: ratingsData.ratingsCount,
                                    title: ratingsData.text,
                                    context: context,
                                    textSize: "smallPlus",
                                    starClass: classNames([
                                        linkedItemStyles.rattingStarText
                                    ]),
                                    ratingClass: classNames([
                                        linkedItemStyles.rattingText
                                    ])
                                }, ratingsData.key)
                            }),
                            /*#__PURE__*/ _jsx(Stack.Item, {
                                children: /*#__PURE__*/ _jsxs(Stack, {
                                    horizontal: true,
                                    className: classNames([
                                        linkedItemStyles.pricingAndCtaToken
                                    ]),
                                    children: [
                                        /*#__PURE__*/ _jsx(Stack.Item, {
                                            className: linkedItemStyles.startingPriceElementContainer,
                                            title: pricingElement === null || pricingElement === void 0 ? void 0 : pricingElement.value,
                                            children: bulidStartingPriceElement({
                                                app: linkedItem,
                                                pricingLinkClassName: 'dark',
                                                billingCountryCode,
                                                context,
                                                locale,
                                                startingPrice
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Stack.Item, {
                                            disableShrink: true,
                                            children: isSaaSRendering ? /*#__PURE__*/ _jsx(DefaultButton, {
                                                primary: isCardHover,
                                                text: context.loc('Button_LearnMore', 'Learn more'),
                                                onClick: (event)=>{
                                                    ctaButtonClick({
                                                        linkedItem,
                                                        event
                                                    });
                                                },
                                                className: classNames([
                                                    linkedItemStyles.ctaButton
                                                ]),
                                                styles: ctaButtonStyle,
                                                title: context.loc('Button_LearnMore', 'Learn more')
                                            }) : /*#__PURE__*/ _jsx(BaseCtaButton, {
                                                app: linkedItem,
                                                billingCountryCode: billingCountryCode,
                                                startingPrice: startingPrice,
                                                isLoadingUserData: isLoadingUserData,
                                                CtaComponent: /*#__PURE__*/ _jsx(DefaultButton, {
                                                    primary: isCardHover,
                                                    text: context.loc('CTA_Get', 'Get it now'),
                                                    onClick: (event)=>{
                                                        ctaButtonClick({
                                                            linkedItem,
                                                            event
                                                        });
                                                    },
                                                    className: classNames([
                                                        linkedItemStyles.ctaButton
                                                    ]),
                                                    styles: ctaButtonStyle,
                                                    title: context.loc('CTA_Get', 'Get it now')
                                                }),
                                                ctaType: Constants.CTAType.Get
                                            })
                                        })
                                    ]
                                })
                            })
                        ]
                    })
                })
            ]
        })
    });
};
LinkedItem.contextTypes = {
    loc: PropTypes.func,
    locParams: PropTypes.func,
    buildHref: PropTypes.func
};
